import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";

import { DisplayHostnameConfig, HostnameConfig, SelectedCustomer, UserInfo } from "./types";
import { toDisplayConfig, toNotConfiguredDisplayConfig } from "./utils";

interface OwnerPropertiesSubset {
  hostnames?: string[];
}

export const useHostnameConfigs = (selectedCustomer: SelectedCustomer, userInfo: UserInfo) => {
  const customerId = selectedCustomer?.customer_id;

  const [hostnameConfigs, setHostnameConfigs] = useState<DisplayHostnameConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const [configsRes, propsRes] = await Promise.all([
        axios.get<{ hostname_configs: HostnameConfig[] }>("/owner/getAllHostnameConfigs", {
          params: { customer_id: customerId },
        }),
        axios.get<{ owner_properties: OwnerPropertiesSubset }>("api/getOwnerProperties", {
          params: { customer_id: customerId },
        }),
      ]);

      const configuredHostnames = configsRes.data.hostname_configs
        .filter((config) => !config.archived)
        .map((config) => toDisplayConfig(config, userInfo));

      // Mirrors getAllHostnameConfigs()'s synthesis of placeholder rows for hostnames
      // the customer owns but that have no HostnameConfig row yet.
      const allHostnames = propsRes.data.owner_properties?.hostnames ?? [];
      const notConfiguredHostnames: DisplayHostnameConfig[] = allHostnames
        .filter((hostname) => configuredHostnames.every((config) => config.hostname !== hostname))
        .map((hostname) => toNotConfiguredDisplayConfig(hostname, userInfo));

      setHostnameConfigs([...notConfiguredHostnames, ...configuredHostnames]);
    } catch (error) {
      generalErrorAlert(error, "There was an error loading your hostname settings, please refresh.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Mirrors updateHostnameConfig(): the backend requires customer_pretty_name,
  // switch_text, show_account_sign_in, and show_bbot_footer on every call, so
  // callers must pass the full current config with only the changed field(s) set.
  const updateHostnameConfig = useCallback(
    async (hostnameConfig: HostnameConfig, successMessage: string) => {
      try {
        await axios.post("/owner/editHostnameConfig", { customerId, hostnameConfig });
        notification.success({ message: successMessage });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "There was an error saving your changes.");
        return false;
      }
    },
    [customerId, load]
  );

  // Mirrors enableBasicDeliveryThemeController's configureBasicDeliveryTheme().
  const enableBasicDeliveryTheme = useCallback(
    async (hostnameConfig: HostnameConfig, locationId: string) => {
      try {
        await axios.post("/owner/editHostnameConfig", {
          customerId,
          isBasicDeliveryTheme: true,
          locationIdForRedirect: locationId,
          hostnameConfig,
        });
        notification.success({ message: "Settings changed successfully!" });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble saving your new settings. Please refresh and try again.");
        return false;
      }
    },
    [customerId, load]
  );

  // Mirrors temporarilyDisableOrderingController's temporarilyDisableOrdering().
  const temporarilyDisableOrdering = useCallback(
    async (hostnameConfig: HostnameConfig, enableOrdering: boolean, richtext?: string) => {
      try {
        await axios.post("/owner/editHostnameConfig", {
          customerId,
          hostnameConfig,
          enableOrdering,
          ...(enableOrdering ? {} : { temporaryDisableRichtext: richtext }),
        });
        notification.success({ message: "Settings changed successfully!" });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble saving your new settings. Please refresh and try again.");
        return false;
      }
    },
    [customerId, load]
  );

  // Mirrors getLiveTemplatesAndInstances(): the rich text currently shown to
  // guests while ordering is temporarily disabled for a bosBuilder hostname.
  const fetchTemporaryDisableText = useCallback(async (hostname: string) => {
    try {
      const res = await axios.get<{ instance_values_by_html_id: Record<string, { richText: string }> }>(
        "/wlbuilder/getLiveTemplatesAndInstances",
        { params: { hostname } }
      );
      return res.data.instance_values_by_html_id["custom-rich-text-top"]?.richText ?? "";
    } catch (error) {
      generalErrorAlert(error, "Trouble loading templates. Please refresh and try again.");
      return "";
    }
  }, []);

  // Mirrors enableLegacyBOS(). The legacy controller sends a bare undeclared
  // customer_id here (always undefined) instead of its own customerId - a
  // real bug, not intentional behavior worth preserving, so this sends the
  // correct value.
  const enableLegacyBos = useCallback(
    async (hostname: string) => {
      try {
        await axios.post("/owner/enableLegacyBosForHostname", { customerId, hostnameConfig: { hostname } });
        notification.success({ message: "Legacy BOS configuration enabled." });
        await load();
      } catch (error) {
        generalErrorAlert(error, "There was an error enabling the legacy BOS configuration for this hostname.");
      }
    },
    [customerId, load]
  );

  // Backs addNewHostname/showArchiveBOSDialog/deleteSelectedHostnameConfigs, which
  // all POST to the same admin endpoint with different payload shapes.
  const editCustomerHostnames = useCallback(
    (payload: Record<string, unknown>) =>
      axios.post("/admin/editCustomerHostnames", { customer_id: customerId, ...payload }),
    [customerId]
  );

  // Mirrors addNewHostnameController's addHostname(). Throws on failure and
  // returns the raw prompt fields on success so the dialog can decide whether
  // to advance to a follow-up step or close - error/success messaging is the
  // dialog's call since it differs by step.
  const addHostname = useCallback(
    async (hostname: string) => {
      const res = await editCustomerHostnames({ newHostname: hostname });
      if (res.data.prompt) {
        return { prompt: res.data.prompt, connectedCustomers: res.data.customers_already_connected as string[] };
      }
      await load();
      return { prompt: null, connectedCustomers: [] as string[] };
    },
    [editCustomerHostnames, load]
  );

  // Mirrors addNewHostnameController's addHostnameFromPrompt(). The caller builds
  // the addHostnameChoice/hostnameToAdd/hostnameToTransfer payload, since which
  // keys apply depends on which prompt step and radio choice is active. Throws
  // on failure.
  const addHostnameFromPrompt = useCallback(
    async (payload: Record<string, unknown>) => {
      await editCustomerHostnames(payload);
      await load();
    },
    [editCustomerHostnames, load]
  );

  // Mirrors archiveBOSDialogController's archiveBOS().
  const archiveHostname = useCallback(
    async (hostname: string) => {
      try {
        await editCustomerHostnames({ hostnameToArchive: hostname });
        notification.success({ message: "Successfully archived BOS." });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "There was a problem archiving this BOS. Please refresh and try again.");
        return false;
      }
    },
    [editCustomerHostnames, load]
  );

  // Mirrors deleteSelectedHostnameConfigs()'s POST + refresh. The last-hostname/
  // all-selected guards and the isAdmin check live in the caller, same as legacy.
  const bulkDeleteHostnames = useCallback(
    async (hostnames: string[]) => {
      try {
        await editCustomerHostnames({ hostnamesToDelete: hostnames });
        notification.success({ message: "Domain(s) removed successfully!" });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Trouble deleting domain. Please refresh and try again");
        return false;
      }
    },
    [editCustomerHostnames, load]
  );

  return {
    hostnameConfigs,
    isLoading,
    refresh: load,
    updateHostnameConfig,
    enableLegacyBos,
    addHostname,
    addHostnameFromPrompt,
    archiveHostname,
    bulkDeleteHostnames,
    enableBasicDeliveryTheme,
    temporarilyDisableOrdering,
    fetchTemporaryDisableText,
  };
};
